import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { axiosInstance } from '../../common/axiosInstans.ts';
import {
    Alert,
    Box,
    Button,
    Container,
    MenuItem,
    Paper,
    Select,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CircularProgress from '@mui/material/CircularProgress';
import { Save } from '@mui/icons-material';

const CreateDrug = () => {
    const [formData, setFormData] = useState({
        drugName: '',
        concentration: '',
        concentrationUnit: '',
        quantity: '',
        quantityUnit: '',
        dosageForm: '',
        atc: '',
        drugIdentifier: '',
        barcode: '',
        gtin: '',
    });
    const [error, setError] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const navigate = useNavigate();

    const handleChange = (e) => {
        const { name, value } = e.target;
        const numericFields = ['concentration', 'quantity'];
        const newValue = numericFields.includes(name)
            ? String(value).replace(/,/g, '.')
            : value;
        setFormData({ ...formData, [name]: newValue });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            setIsLoading(true);
            await axiosInstance.post('/drug', formData);
            navigate('..');
        } catch (error) {
            setError(error.response.data.exception);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Container maxWidth="sm">
            <Box display="flex" alignItems="center" mb={2}>
                <Button
                    startIcon={<ArrowBackIcon />}
                    onClick={() => navigate('..')}
                    color="primary"
                >
                    Tillbaka
                </Button>
            </Box>
            <Paper elevation={3} sx={{ p: 4 }}>
                <Typography variant="h4" align="center" gutterBottom>
                    Nytt läkemedel
                </Typography>
                <Box mt={2} mb={2} borderBottom={1} borderColor="divider" />
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}
                <form onSubmit={handleSubmit}>
                    <Stack spacing={2}>
                        <TextField
                            label="Namn på läkemedlet"
                            name="drugName"
                            value={formData.drugName}
                            onChange={handleChange}
                            required
                            fullWidth
                        />
                        <Stack direction="row" spacing={2}>
                            <TextField
                                label="Styrka"
                                name="concentration"
                                type="number"
                                value={formData.concentration}
                                onChange={handleChange}
                                required
                                fullWidth
                            />
                            <Select
                                name="concentrationUnit"
                                value={formData.concentrationUnit}
                                onChange={handleChange}
                                required
                                fullWidth
                            >
                                {[
                                    'g',
                                    'mg',
                                    'µg',
                                    'ml',
                                    'mg/ml',
                                    'µg/ml',
                                    'IE',
                                    'IE/ml',
                                    'st',
                                    '%',
                                ].map((unit) => (
                                    <MenuItem key={unit} value={unit}>
                                        {unit}
                                    </MenuItem>
                                ))}
                            </Select>
                        </Stack>
                        <Stack direction="row" spacing={2}>
                            <TextField
                                label="Mängd"
                                name="quantity"
                                type="number"
                                value={formData.quantity}
                                onChange={handleChange}
                                required
                                fullWidth
                            />
                            <Select
                                name="quantityUnit"
                                value={formData.quantityUnit}
                                onChange={handleChange}
                                required
                                fullWidth
                            >
                                <MenuItem value="">
                                    <em>Välj...</em>
                                </MenuItem>
                                {[
                                    'g',
                                    'mg',
                                    'µg',
                                    'ml',
                                    'mg/ml',
                                    'µg/ml',
                                    'IE',
                                    'IE/ml',
                                    'st',
                                    '%',
                                ].map((unit) => (
                                    <MenuItem key={unit} value={unit}>
                                        {unit}
                                    </MenuItem>
                                ))}
                            </Select>
                        </Stack>
                        <TextField
                            select
                            label="Beredningsform"
                            name="dosageForm"
                            value={formData.dosageForm}
                            onChange={handleChange}
                            fullWidth
                            required
                        >
                            {[
                                'Injektionsvätska, Lösning',
                                'Injektionsvätska, Emulsion',
                                'Rektallösning',
                                'Tablett',
                                'Injektions-/Infusionsvätska',
                                'Depåkapsel',
                                'Depåplåster',
                                'Lösning för nebulisator',
                                'Nasal spray',
                                'Sublingualspray',
                                'Inhalationsånga, vätska',
                            ].map((form) => (
                                <MenuItem key={form} value={form}>
                                    {form}
                                </MenuItem>
                            ))}
                        </TextField>
                        <TextField
                            label="ATC"
                            name="atc"
                            value={formData.atc}
                            onChange={handleChange}
                            fullWidth
                        />
                        <TextField
                            label="Unik identifierare"
                            name="drugIdentifier"
                            value={formData.drugIdentifier}
                            onChange={handleChange}
                            fullWidth
                        />
                        <TextField
                            label="Streckkod"
                            name="barcode"
                            value={formData.barcode}
                            onChange={handleChange}
                            fullWidth
                        />
                        <TextField
                            label="GTIN"
                            name="gtin"
                            value={formData.gtin}
                            onChange={handleChange}
                            fullWidth
                        />
                    </Stack>
                    <Stack
                        direction="row"
                        spacing={2}
                        justifyContent="center"
                        mt={3}
                    >
                        <Button
                            variant="outlined"
                            color="primary"
                            onClick={() => navigate('..')}
                        >
                            Avbryt
                        </Button>
                        <Button
                            startIcon={
                                isLoading ? (
                                    <CircularProgress size={24} color="info" />
                                ) : (
                                    <Save />
                                )
                            }
                            variant="contained"
                            color="primary"
                            type="submit"
                        >
                            Spara läkemedel
                        </Button>
                    </Stack>
                </form>
            </Paper>
        </Container>
    );
};

export default CreateDrug;
